import { apiRole } from "@/api/role.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiCustomFieldRoles } from "@/types/custom-field.types.ts";
import { ApiFolderRoles } from "@/types/document.types.ts";
import { ApiRoleSavePayload } from "@/types/role.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const ROLES_POLL_INTERVAL_MS = 3000;

const rolesKeys = createQueryKeys("roles", {
  list: null,
  detail: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (!id) throw new ApiPassException("Role id is required");

      const result = await apiRole.getById(id);

      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useRoleById = (id: number | undefined) => {
  return useQuery({
    ...rolesKeys.detail(id),
    enabled: !!id,
  });
};

export const useRoles = (enabled: boolean = true, pollWhileRecalculating: boolean = false) => {
  return useQuery({
    ...rolesKeys.list,
    queryFn: async () => {
      const result = await apiRole.getAll();

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    enabled: enabled,
    refetchInterval: pollWhileRecalculating
      ? (query) =>
          query.state.data?.some((role) => role.is_recalculating) ? ROLES_POLL_INTERVAL_MS : false
      : undefined,
  });
};

export interface RolesSaveParams {
  id: number;
  payload: ApiRoleSavePayload;
}

export const useRolesSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: RolesSaveParams) {
      const result = id == 0 ? await apiRole.create(payload) : await apiRole.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (role) => {
      queryClient.setQueryData(rolesKeys.detail(role.id).queryKey, role);
      void queryClient.invalidateQueries({ queryKey: rolesKeys.list.queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useRolesDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function (id: number) {
      const { success, error } = await apiRole.delete(id);

      if (error || !success) throw new ApiErrorException(error);

      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData(rolesKeys.detail(id).queryKey, undefined);
      void queryClient.invalidateQueries(rolesKeys.list);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useRolesCustomField = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (payload: ApiCustomFieldRoles) {
      const { success, error } = await apiRole.customFields(payload);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useRolesFolders = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (payload: ApiFolderRoles) {
      const { success, error } = await apiRole.folders(payload);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
